import {t, useSiteLanguage} from "../../../i18n";
import {jourDeLaSemaine, joursDuLabel, OpeningRange, statutMaintenant, JOURS} from './openingHours';

/**
 * L'état du restaurant, en une pastille.
 *
 * Le mot porte l'information, la couleur ne fait que la renforcer : « Ouvert » et « Fermé » sont
 * écrits, et la pastille reste lisible pour qui ne distingue pas le vert du gris.
 *
 * `maintenant` est passé plutôt que lu ici : c'est ce qui rend le composant vérifiable à
 * n'importe quelle heure, et la logique qu'il affiche est testée à part.
 */
export const StatutOuverture = ({
    horaires,
    maintenant = new Date(),
}: {
    horaires: readonly OpeningRange[];
    maintenant?: Date;
}) => {
    const locale = useSiteLanguage();
    const statut = statutMaintenant(horaires, maintenant);
    if (statut.heure === null) return null;

    const suite = statut.ouvert
        ? t('hours.closes', {time: statut.heure})
        : t('hours.opens', {time: statut.heure, day: statut.jour === null ? '' : `${dayName(JOURS.indexOf(statut.jour as typeof JOURS[number]), locale, 'long')} `});

    return (
        <p className={`hours-status ${statut.ouvert ? 'is-open' : 'is-closed'}`}>
            <span className="hours-status__dot" aria-hidden="true" />
            <strong>{statut.ouvert ? t("hours.open") : t("hours.closed")}</strong>
            <span className="hours-status__detail">{suite}</span>
        </p>
    );
};

/** Trois lettres, comme « Lun — Ven ». */
const abrege = (label: string): string =>
    label
        .split('-')
        .map((mot) => mot.trim().substring(0, 3))
        .join(' — ');

/**
 * La liste des horaires, la ligne du jour mise en avant.
 *
 * Un visiteur cherche d'abord *aujourd'hui*. La marquer lui évite de relire deux lignes pour
 * savoir laquelle le concerne — et le mot « aujourd'hui » l'accompagne, la mise en avant seule
 * ne disant rien à qui ne la perçoit pas.
 */
export const ListeHoraires = ({
    horaires,
    maintenant = new Date(),
}: {
    horaires: readonly OpeningRange[];
    maintenant?: Date;
}) => {
    const locale = useSiteLanguage();
    const aujourdhui = jourDeLaSemaine(maintenant);

    return (
        <ul className="hours-list">
            {horaires.map((jour) => {
                const courant = joursDuLabel(jour.label).includes(aujourdhui);
                return (
                    <li key={jour.label} className={`hours-row ${courant ? 'is-today' : ''}`}>
                        <span className="hours-row__day">
                            {locale === 'fr' ? abrege(jour.label) : jour.label.split('-').map(label => dayName(JOURS.indexOf(label.trim().toLowerCase() as typeof JOURS[number]), locale, 'short')).join(' — ')}
                            {courant && <em className="hours-row__today">{t("hours.today")}</em>}
                        </span>
                        <span className="hours-row__time">
                            {jour.horaires[0]} – {jour.horaires[1]}
                        </span>
                    </li>
                );
            })}
        </ul>
    );
};

function dayName(index: number, locale: string, weekday: 'short' | 'long') {
    return new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : 'fr-FR', {weekday, timeZone: 'UTC'}).format(new Date(Date.UTC(2024, 0, 1 + index)));
}
